import React from "react";
import { Text, Badge, Stack, Group, Grid, Anchor, Tooltip } from "@mantine/core";
import { useTranslation } from "react-i18next";
import EntityTabAnchor from "./EntityTabAnchor";
import { incentiveTypeBadge } from "../../../data/options";

const getDnStatusColor = (status) => {
  switch (status) {
    case "DFT":
      return "yellow";
    case "WAIT":
    case "HOLD":
      return "red";
    case "CONFIRMED":
    case "SETTLED":
      return "blue";
    case "EXP":
      return "green";
    case "CAN":
      return "orange";
    default:
      return "blue";
  }
};

const getPolicyStatusColor = (status) => {
  if (status === "POLICY_RECEIVED") return "green";
  if (status === "POLICY_DISPATCHED") return "blue";
  return "gray";
};

const getSourceLabel = (order) => {
  if (order.sourceType === "CLIENTPAYMENT") return "Client Payment";
  if (order.sourceType === "COMMISSIONSTATEMENT") return "Comm. Statement";
  return order.sourceType || "Order";
};

const getIncentiveTypeLabel = (incentive) =>
  incentiveTypeBadge(incentive?.incentiveType).label;

const renderSourcePayrollBadges = (payrolls = []) => (
  <>
    {(payrolls || []).map((payroll) => (
      <EntityTabAnchor
        key={payroll._id}
        entity="payroll"
        id={payroll._id}
        label={payroll.code}
        size="xs"
        weight={500}
        onClick={(e) => e.stopPropagation()}
      />
    ))}
    {payrolls?.length > 1 && (
      <Tooltip label="Native bids for this source exist on multiple payrolls">
        <Badge size="xs" color="red" variant="filled">
          Duplicate payrolls
        </Badge>
      </Tooltip>
    )}
  </>
);

const HoldingByOrderRowHeader = ({ order, formatter, cellRender, rowNumber }) => {
  const { t } = useTranslation();

  if (order.groupKind === "INCENTIVE" || order.incentive) {
    const incentive = order.incentive;
    return (
      <Grid justify="space-between" align="center">
        {rowNumber != null && (
          <Grid.Col span={1}>
            <Text size="sm" color="dimmed" weight={600} align="center">
              {rowNumber}
            </Text>
          </Grid.Col>
        )}
        <Grid.Col span={rowNumber != null ? 6 : 7}>
          <Stack spacing={4}>
            <Group spacing="xs">
              <Badge
                size="sm"
                color={incentiveTypeBadge(incentive?.incentiveType).color}
                variant="light"
              >
                {getIncentiveTypeLabel(incentive)}
              </Badge>
              {order.holdCount > 0 && (
                <Badge size="xs" color="orange" variant="filled">
                  {order.holdCount} hold
                </Badge>
              )}
              {(order.payrolls || []).length > 0 && renderSourcePayrollBadges(order.payrolls)}
              {order.cancelCount > 0 && (
                <Badge size="xs" color="red" variant="filled">
                  {order.cancelCount} cancel
                </Badge>
              )}
            </Group>
            <Group spacing="xs">
              {incentive?._id ? (
                <Anchor
                  href={`/m/account/incentive?id=${incentive._id}&mode=edit`}
                  target="_blank"
                  size="sm"
                  weight={500}
                  onClick={(e) => e.stopPropagation()}
                >
                  {incentive.code || "Incentive"}
                </Anchor>
              ) : (
                <Text size="sm" weight={500}>
                  {order.key || "Incentive/Charge"}
                </Text>
              )}
            </Group>
            {incentive?.title && (
              <Group spacing="xs">
                <Text size="xs" color="dimmed">
                  Title:
                </Text>
                <Text size="xs" weight={500}>
                  {incentive.title}
                </Text>
              </Group>
            )}
            {incentive?.consultant && (
              <Group spacing="xs">
                <Text size="xs" color="dimmed">
                  Consultant:
                </Text>
                <Text size="xs" weight={500}>
                  {incentive.consultant.name || incentive.consultant.code}
                </Text>
              </Group>
            )}
          </Stack>
        </Grid.Col>
        <Grid.Col span={2}>
          <Text size="sm" weight={600}>
            {order.bidCount} bids
          </Text>
        </Grid.Col>
        <Grid.Col span={3}>
          <Text size="sm" weight={600} color="orange">
            {formatter.currency(order.totalAmount)}
          </Text>
        </Grid.Col>
      </Grid>
    );
  }

  if (order.groupKind === "UNLINKED") {
    return (
      <Grid justify="space-between" align="center">
        {rowNumber != null && (
          <Grid.Col span={1}>
            <Text size="sm" color="dimmed" weight={600} align="center">
              {rowNumber}
            </Text>
          </Grid.Col>
        )}
        <Grid.Col span={rowNumber != null ? 6 : 7}>
          <Stack spacing={4}>
            <Group spacing="xs">
              <Badge size="sm" color="grape" variant="light">
                {order.sourceType || "Unlinked"}
              </Badge>
              {order.holdCount > 0 && (
                <Badge size="xs" color="orange" variant="filled">
                  {order.holdCount} hold
                </Badge>
              )}
              {order.cancelCount > 0 && (
                <Badge size="xs" color="red" variant="filled">
                  {order.cancelCount} cancel
                </Badge>
              )}
            </Group>
            <Text size="xs" color="dimmed">
              Holding bids without order or debit note
            </Text>
          </Stack>
        </Grid.Col>
        <Grid.Col span={2}>
          <Text size="sm" weight={600}>
            {order.bidCount} bids
          </Text>
        </Grid.Col>
        <Grid.Col span={3}>
          <Text size="sm" weight={600} color="orange">
            {formatter.currency(order.totalAmount)}
          </Text>
        </Grid.Col>
      </Grid>
    );
  }

  const dnStatus = order.debitNote?.status ?? order.filterMeta?.dnStatus;
  const orderStatus = order.order?.status ?? order.filterMeta?.orderStatus;
  const policyStatus =
    order.debitNote?.policyStatus ??
    order.filterMeta?.dnPolicyStatus ??
    order.order?.policyStatus ??
    null;

  const client = order.debitNote?.client || order.order?.client;
  const product =
    order.debitNote?.product ||
    order.debitNote?.productType ||
    order.order?.product ||
    order.order?.productType;

  return (
    <Grid justify="space-between" align="center">
      {rowNumber != null && (
        <Grid.Col span={1}>
          <Text size="sm" color="dimmed" weight={600} align="center">
            {rowNumber}
          </Text>
        </Grid.Col>
      )}
      <Grid.Col span={rowNumber != null ? 6 : 7}>
        <Group spacing="xs" align="flex-start">
          <Stack spacing={4}>
            <Group spacing="xs">
              <Badge size="sm" color="blue" variant="light">
                {getSourceLabel(order)}
              </Badge>
              {order.holdCount > 0 && (
                <Badge size="xs" color="orange" variant="filled">
                  {order.holdCount} hold
                </Badge>
              )}
              {(order.payrolls || []).length > 0 && renderSourcePayrollBadges(order.payrolls)}
              {order.cancelCount > 0 && (
                <Badge size="xs" color="red" variant="filled">
                  {order.cancelCount} cancel
                </Badge>
              )}
            </Group>
            <Group spacing="xs">
              {order.debitNote?.code && (
                <EntityTabAnchor
                  entity="debitNote"
                  id={order.debitNote._id}
                  label={order.debitNote.code}
                  size="sm"
                  weight={500}
                  onClick={(e) => e.stopPropagation()}
                />
              )}
              {order.order?.code && (
                <EntityTabAnchor
                  entity="order"
                  id={order.order._id}
                  label={order.order.code}
                  size="sm"
                  weight={500}
                  onClick={(e) => e.stopPropagation()}
                />
              )}
              {order.debitNote && dnStatus && (
                <Badge size="xs" radius={0} color={getDnStatusColor(dnStatus)}>
                  {t(dnStatus)}
                </Badge>
              )}
              {!order.debitNote && orderStatus && cellRender?.Order?.status && (
                cellRender.Order.status(orderStatus)
              )}
              {!order.debitNote && orderStatus && !cellRender?.Order?.status && (
                <Badge size="xs" radius={0} color="blue">
                  {t(orderStatus)}
                </Badge>
              )}
              {policyStatus && (
                <Badge
                  size="xs"
                  radius={0}
                  color={getPolicyStatusColor(policyStatus)}
                >
                  {policyStatus}
                </Badge>
              )}
            </Group>
            {client && (
              <Group spacing="xs">
                <Text size="xs" color="dimmed">
                  Client:
                </Text>
                <EntityTabAnchor
                  entity="client"
                  id={client._id}
                  label={client.name || client.code}
                  size="xs"
                  weight={500}
                  onClick={(e) => e.stopPropagation()}
                />
                {client.cname && (
                  <Text size="xs" color="dimmed">
                    ({client.cname})
                  </Text>
                )}
                {client.code && (
                  <Badge size="xs" variant="outline" color="gray">
                    {client.code}
                  </Badge>
                )}
              </Group>
            )}
            {product && (
              <Group spacing="xs">
                <Text size="xs" color="dimmed">
                  Product:
                </Text>
                <Text size="xs" weight={500}>
                  {product.name || product.code}
                </Text>
                {product.cname && (
                  <Text size="xs" color="dimmed">
                    ({product.cname})
                  </Text>
                )}
              </Group>
            )}
          </Stack>
        </Group>
      </Grid.Col>
      <Grid.Col span={2}>
        <Text size="sm" weight={600}>
          {order.bidCount} bids
        </Text>
      </Grid.Col>
      <Grid.Col span={3}>
        <Text size="sm" weight={600} color="orange">
          {formatter.currency(order.totalAmount)}
        </Text>
      </Grid.Col>
    </Grid>
  );
};

export default HoldingByOrderRowHeader;
